import { action, computed, makeObservable, observable } from 'mobx'
import { isPending, trackedAction } from 'mobx-async'

import { RefundReason } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { SidePanelStore, SidePanelType } from '@new/__global/stores/SidePanelStore'
// eslint-disable-next-line import/no-cycle
import { UiStore } from '@new/__global/stores/UiStore'
import { BookingStore } from '@new/domains/bookings/stores/BookingStore'

import bookings from '@arriere/modules/bookings'
import { RefundMethodView } from '@arriere/types/BookingAdjustment.types'

import { BookingFolioItemProps, IRefundablePayment, IRefundPaymentsParams } from './_types'
import { handleSubmitError } from './BookingFolioStore'

class _RefundStore {
  refundableItem: BookingFolioItemProps | null = null

  refundablePayments = observable.array<IRefundablePayment>([])

  constructor() {
    makeObservable(this, {
      refundableItem: observable,
      isSelectable: computed,
      isOpen: computed,
      isSubmitting: computed,
      refundReasons: computed({ keepAlive: true }),
      setRefundableItem: action.bound,
      openRefundForm: action.bound,
      closeForm: action.bound,
      openPaymentServicesRefundDrawer: action.bound,
      closePaymentServicesRefundDrawer: action.bound,
    })

    this.refundPaymentMethods = trackedAction<any>(this.refundPaymentMethods.bind(this))
    this.refundPayments = trackedAction<any>(this.refundPayments.bind(this))
  }

  get isSelectable() {
    return this.refundablePayments.length > 1
  }

  get isOpen() {
    return this.refundablePayments.length > 0
  }

  get isSubmitting() {
    return isPending(this.refundPayments)
  }

  get refundReasons(): Promise<RefundReason[]> {
    return bookings.getRefundReasons()
  }

  setRefundableItem(item: BookingFolioItemProps | null): void {
    this.refundableItem = item
  }

  openRefundForm(refundablePayments: IRefundablePayment | IRefundablePayment[]) {
    this.refundablePayments.replace(
      Array.isArray(refundablePayments) ? refundablePayments : [refundablePayments],
    )
  }

  closeForm() {
    this.refundablePayments.length = 0

    if (SidePanelStore.currentSidePanel) {
      this.closePaymentServicesRefundDrawer()
    }
  }

  openPaymentServicesRefundDrawer(
    refundablePayments: IRefundablePayment | IRefundablePayment[],
  ): void {
    this.openRefundForm(refundablePayments)
    SidePanelStore.setCurrentSidePanel(SidePanelType.paymentAndServicesRefund)
  }

  closePaymentServicesRefundDrawer(): void {
    UiStore.setShouldDisplayFilterSideBar(false)
    SidePanelStore.setCurrentSidePanel()
  }

  async refundPayments({ paymentsToRefund, reason }: IRefundPaymentsParams) {
    return bookings
      .refundBookingCharges({
        paymentsToRefund,
        reason,
      })
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async refundPaymentMethods(methods: RefundMethodView[]) {
    return BookingStore.booking.then(_booking => {
      if (!_booking) {
        handleSubmitError('There was an error submitting the refund.')
        return false
      }

      return bookings.refundPaymentSource(_booking.id, methods).then(success => {
        if (success) {
          Alert.Success('Payments refunded successfully.')
        } else {
          handleSubmitError('There was an error submitting the refund.')
        }
        return success
      })
    })
  }
}

export const RefundStore = new _RefundStore()
